/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import ConsignmentHero from './components/ConsignmentHero';
import ConsignmentBenefits from './components/ConsignmentBenefits';
import ConsignmentHowItWorks from './components/ConsignmentHowItWorks';
import ConsignmentComparison from './components/ConsignmentComparison';
import WhatWeLookFor from './components/WhatWeLookFor';
import ConsignmentForm from './components/ConsignmentForm';
import ConsignmentFaq from './components/ConsignmentFaq';
import ConsignmentCta from './components/ConsignmentCta';
import { useSEOHead } from '../../hooks/useSEOHead';
import { useGenericPageSchema } from '../../hooks/useStructuredData';
import { injectJsonLd, removeJsonLd, buildBreadcrumbSchema } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import Breadcrumb from '../../components/feature/Breadcrumb';

const BASE = 'https://rockhoundauto.com';

const CONSIGNMENT_FAQS = [
  {
    question: 'How long will it take to sell my car?',
    answer: 'Most vehicles sell within 30–45 days. As a rule of thumb, we aim to turn our entire consignment inventory within 60 days. Pricing, condition, and market demand all play a role, and we\'ll keep you updated throughout the process.',
  },
  {
    question: 'How does Rockhound Auto charge for consignment?',
    answer: 'We charge a transparent flat fee on all consignments — no hidden costs or surprise deductions. We\'ll work with you to set the right asking price and communicate every offer that comes in so you stay in control.',
  },
  {
    question: 'Can you help me determine what my car is worth?',
    answer: 'Absolutely. Our team evaluates all aspects of your vehicle and the current market to determine the best listing price. We combine dealer appraisal tools, auction data, and years of experience to help you reach the right market value.',
  },
  {
    question: 'What if I still have a bank loan on my car?',
    answer: 'No problem at all. We handle the entire process from loan payoff to title transfer. Once the car sells, we take care of everything and cut you a check for the difference.',
  },
  {
    question: 'Can buyers trade in their car on my consignment vehicle?',
    answer: 'Yes! We treat every car on our lot the same, whether we own it or it\'s on consignment. Accepting trade-ins gives your vehicle access to the full benefits of being in our inventory and helps it sell faster.',
  },
  {
    question: 'Can a buyer finance my consignment car?',
    answer: 'Yes. We work with a wide variety of lending institutions that offer financing regardless of the buyer\'s credit score. This means your consignment vehicle has a much higher chance of selling at a strong price compared to a private sale.',
  },
  {
    question: 'Is my car insured while on your lot?',
    answer: 'Yes. Your vehicle is covered under our dealer insurance policy while it\'s in our care. You can rest easy knowing it\'s protected.',
  },
  {
    question: 'What types of vehicles do you accept for consignment?',
    answer: 'We accept cars, trucks, SUVs, and vans of all makes and models. Whether it\'s a $10,000 commuter or a $75,000 truck, we\'ll give it the same professional treatment and marketing effort.',
  },
];

export default function ConsignmentPage() {
  useSEOHead({
    title: 'Consignment | Sell for More | Rockhound Auto',
    description: 'Consign your vehicle with Rockhound Auto and get top dollar. We handle reconditioning, advertising, financing, negotiations, and paperwork — you just wait for the check.',
    keywords: 'auto consignment, sell car on consignment, consignment car sales, Pacific Northwest',
    canonicalUrl: `${BASE}/consignment`,
    ogImage: 'https://public.readdy.ai/ai/img_res/91c47124038984e265ae1ced1e8aebc6.jpg',
    ogType: 'website',
    ogImageAlt: 'Rockhound Auto consignment services - professional vehicle sales with transparent pricing and expert marketing',
  });

  useGenericPageSchema({
    type: 'WebPage',
    name: 'Auto Consignment Services | Sell Your Car for More | Rockhound Auto',
    description: 'Consign your vehicle with Rockhound Auto and get top dollar.',
    url: `${BASE}/consignment`,
    schemaId: 'consignment',
    breadcrumbs: [
      { name: 'Home', item: BASE },
      { name: 'Consignment', item: `${BASE}/consignment` },
    ],
  });

  // FAQPage schema for rich results
  useEffect(() => {
    injectFAQSchema(
      'schema-consignment-faq',
      CONSIGNMENT_FAQS,
      `${BASE}/consignment#faqpage`
    );

    // Explicit BreadcrumbList schema
    const breadcrumbSchema = buildBreadcrumbSchema([
      { name: 'Home', item: BASE },
      { name: 'Consignment', item: `${BASE}/consignment` },
    ]);
    injectJsonLd('schema-consignment-breadcrumb-explicit', breadcrumbSchema);

    // ── Service schema with priceSpecification ────────────────────────────
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${BASE}/consignment#service`,
      name: 'Vehicle Consignment Service',
      alternateName: 'Auto Consignment Oregon Coast',
      description:
        'Sell your car for more with Rockhound Auto\'s consignment service. We handle reconditioning, professional photography, advertising, test drives, financing, negotiations, and all paperwork. You keep ownership until it sells — then collect your check.',
      url: `${BASE}/consignment`,
      serviceType: 'Vehicle Consignment',
      category: 'Automotive',
      provider: {
        '@type': 'AutoDealer',
        '@id': `${BASE}/#localbusiness`,
        name: 'Rockhound Auto',
        url: BASE,
        telephone: '(855) 468-6348',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1935 SE Ensign Ln',
          addressLocality: 'Warrenton',
          addressRegion: 'OR',
          postalCode: '97146',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'City', name: 'Warrenton', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Astoria', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Seaside', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Tillamook', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Long Beach', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Portland', containedInPlace: { '@type': 'State', name: 'Oregon' } },
      ],
      // ── priceSpecification: 10–15% commission ──────────────────────────
      offers: {
        '@type': 'Offer',
        '@id': `${BASE}/consignment#offer`,
        description: 'Consignment commission — 10% to 15% of final sale price, covering all marketing, showings, financing, and paperwork.',
        priceCurrency: 'USD',
        priceSpecification: [
          {
            '@type': 'UnitPriceSpecification',
            name: 'Standard Consignment Commission',
            description: 'Commission charged on final sale price. Covers professional photography, online listings, showroom display, test drives, financing coordination, and all sales paperwork.',
            referenceQuantity: {
              '@type': 'QuantitativeValue',
              value: '1',
              unitText: 'vehicle sale',
            },
            minPrice: 10,
            maxPrice: 15,
            priceCurrency: 'USD',
            valueAddedTaxIncluded: false,
            eligibleQuantity: {
              '@type': 'QuantitativeValue',
              minValue: 1,
            },
          },
          {
            '@type': 'PriceSpecification',
            name: 'No Upfront Fees',
            description: 'Zero fees unless your vehicle sells. No listing fee, no photography fee, no storage fee.',
            price: 0,
            priceCurrency: 'USD',
          },
        ],
        availability: 'https://schema.org/InStock',
        validFrom: new Date().toISOString().split('T')[0],
        seller: {
          '@type': 'AutoDealer',
          name: 'Rockhound Auto',
          url: BASE,
        },
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Consignment Services Included',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Professional Photography & Listing',
              description: 'High-quality photos and detailed listings on all major automotive platforms.',
            },
            price: 0,
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Buyer Financing Coordination',
              description: 'We arrange financing for buyers, expanding your pool of qualified purchasers.',
            },
            price: 0,
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Test Drive & Showing Management',
              description: 'Our staff handles all test drives, showings, and buyer interactions — you never deal with strangers.',
            },
            price: 0,
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Title Transfer & Paperwork',
              description: 'Complete title transfer, bill of sale, and DMV paperwork handled by our team.',
            },
            price: 0,
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Loan Payoff Coordination',
              description: 'If your vehicle is financed, we coordinate directly with your lender for a seamless payoff and title release.',
            },
            price: 0,
            priceCurrency: 'USD',
          },
        ],
      },
      termsOfService: `${BASE}/terms-of-service`,
      availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: `${BASE}/consignment`,
        servicePhone: '(855) 468-6348',
        availableLanguage: 'English',
        serviceLocation: {
          '@type': 'Place',
          name: 'Rockhound Auto — Warrenton, OR',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '1935 SE Ensign Ln',
            addressLocality: 'Warrenton',
            addressRegion: 'OR',
            postalCode: '97146',
            addressCountry: 'US',
          },
        },
      },
      // Typical time to sell
      serviceOutput: {
        '@type': 'Thing',
        name: 'Vehicle Sale',
        description: 'Most consignment vehicles sell within 30–60 days at prices 15–25% higher than instant buyout offers.',
      },
    };
    injectJsonLd('schema-consignment-service', serviceSchema);

    return () => {
      removeFAQSchema('schema-consignment-faq');
      removeJsonLd('schema-consignment-breadcrumb-explicit');
      removeJsonLd('schema-consignment-service');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Consignment' }]} />
      <ConsignmentHero />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Expert Perspective Block */}
        <div className="mb-16 bg-gradient-to-br from-indigo-50 to-purple-50 border-l-4 border-indigo-500 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                <i className="ri-auction-line text-2xl text-white"></i>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Expert Perspective</h3>
              <p className="text-gray-700 leading-relaxed">
                Consignment makes sense for vehicles worth $15,000 or more where retail-wholesale spread justifies the wait. We handle marketing, showings, test drives, and paperwork—you just wait for the check. Most consignments sell within 30-45 days at prices 15-25% higher than instant buyout offers. Patience pays.
              </p>
            </div>
          </div>
        </div>
        <ConsignmentHowItWorks />
        <WhatWeLookFor />
        <ConsignmentBenefits />
        <ConsignmentComparison />
        <ConsignmentFaq />
        <ConsignmentForm />
        <ConsignmentCta />
      </div>
    </>
  );
}